import { ComplianceDocument } from '@/lib/types';
import { getSharedPdfWorker, pdfjsLib } from '@/lib/pdfjs';

// Each thumbnail downloads the whole PDF (the bucket exposes no range support).
export const MAX_THUMBNAIL_SOURCE_BYTES = 20 * 1024 * 1024;
// Small PDFs are latency-bound, so parallel requests fill a screen faster.
export const THUMBNAIL_CONCURRENCY = 6;
// Card preview width; every slot shares this render so one cache entry serves all.
export const THUMBNAIL_RENDER_WIDTH = 248;

export const canRenderPdfThumbnail = ({
  extension,
  preview_url,
  size,
}: Pick<ComplianceDocument, 'extension' | 'preview_url' | 'size'>) =>
  extension?.trim().toLowerCase() === 'pdf' &&
  !!preview_url?.trim() &&
  (size == null || size <= MAX_THUMBNAIL_SOURCE_BYTES);

// Kept outside the 'compliance-documents' prefix so upload invalidation
// doesn't re-render every thumbnail.
export const complianceThumbnailQueryKey = (
  id: number,
  updatedAt?: string,
) =>
  ['compliance-document-thumbnail', id, updatedAt ?? null] as const;

export const createLimiter = (max: number) => {
  let active = 0;
  const queue: (() => void)[] = [];
  const priorityQueue: (() => void)[] = [];

  const next = () => {
    if (active >= max) return;
    (priorityQueue.shift() ?? queue.shift())?.();
  };

  return <T>(
    job: () => Promise<T>,
    { priority = false }: { priority?: boolean } = {},
  ): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      (priority ? priorityQueue : queue).push(() => {
        active += 1;
        Promise.resolve()
          .then(job)
          .then(resolve, reject)
          .finally(() => {
            active -= 1;
            next();
          });
      });
      next();
    });
};

const limit = createLimiter(THUMBNAIL_CONCURRENCY);

const abortError = () => new DOMException('Aborted', 'AbortError');

export const renderPdfThumbnail = (
  url: string,
  { width, priority }: { width: number; priority?: boolean },
  signal?: AbortSignal,
): Promise<string> => {
  if (signal?.aborted) return Promise.reject(abortError());

  return limit(
    async () => {
      if (signal?.aborted) throw abortError();

      const task = pdfjsLib.getDocument({
        url,
        worker: getSharedPdfWorker(),
        disableAutoFetch: true,
      });
      // pdf.js never settles a document destroyed mid-download, so abort
      // rejects on its own; otherwise the job would keep its slot.
      let onAbort = () => {};
      const aborted = new Promise<never>((_, reject) => {
        onAbort = () => reject(abortError());
      });
      const orAbort = <T>(promise: Promise<T>) =>
        Promise.race([promise, aborted]);
      signal?.addEventListener('abort', onAbort);

      try {
        const pdf = await orAbort(task.promise);
        const page = await orAbort(pdf.getPage(1));
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const scale =
          (width / page.getViewport({ scale: 1 }).width) * pixelRatio;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        await orAbort(page.render({ canvas, viewport }).promise);

        return canvas.toDataURL('image/jpeg', 0.85);
      } finally {
        signal?.removeEventListener('abort', onAbort);
        await task.destroy();
      }
    },
    { priority },
  );
};
